/**
 * OAuth callbacks under /auth/ (e.g. /auth/mcp-server/callback,
 * /auth/windows-app/callback) receive one-time credentials in the `code` and
 * `state` query parameters. Telemetry must never persist them, so reported
 * Consent routes also receive a CSRF credential in `csrf_token`. URLs pass
 * through these redactors before leaving the browser. Redaction
 * replaces the values instead of dropping the parameters so telemetry keeps
 * the signal that a code was present.
 */
const AUTH_PATH_PREFIX = '/auth/';
const SENSITIVE_AUTH_PARAMS = new Set(['code', 'csrf_token', 'state']);
const USER_AUTHORED_SEARCH_PARAMS_BY_PATH = {
    '/computer/connectors': ['query', 'q'],
    '/library': ['q'],
    '/projects': ['q'],
    '/rest/enterprise/user/organization/members': ['email-filter', 'email_filter'],
    '/rest/sources': ['search'],
    '/search/new': ['q'],
    '/spaces': ['q'],
};
const REDACTED_VALUE = 'redacted';
/**
 * Removes query strings and fragments while preserving the URL path. This
 * intentionally avoids URL parsing so malformed URL-like telemetry cannot
 * bypass redaction.
 */
export function redactUrlQueryData(url) {
    return url.replace(/[?#].*$/s, '');
}
export function redactAuthSearchParams(pathname, search) {
    if (!search || !pathname.startsWith(AUTH_PATH_PREFIX)) {
        return search;
    }
    const params = new URLSearchParams(search);
    const present = [...SENSITIVE_AUTH_PARAMS].filter((name) => params.has(name));
    if (present.length === 0) {
        return search;
    }
    for (const name of present) {
        params.set(name, REDACTED_VALUE);
    }
    return `?${params.toString()}`;
}
/**
 * Redacts user-authored or credential-bearing values from URLs before they
 * leave the process through analytics, RUM, or tracing.
 */
export function redactTelemetrySearchParams(pathname, search) {
    const authRedactedSearch = redactAuthSearchParams(pathname, search);
    const normalizedPathname = pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
    const userAuthoredSearchParams = USER_AUTHORED_SEARCH_PARAMS_BY_PATH[normalizedPathname];
    if (!userAuthoredSearchParams) {
        return authRedactedSearch;
    }
    const params = new URLSearchParams(authRedactedSearch);
    if (!userAuthoredSearchParams.some((name) => params.has(name))) {
        return authRedactedSearch;
    }
    userAuthoredSearchParams.forEach((name) => params.delete(name));
    const redactedSearch = params.toString();
    return redactedSearch ? `?${redactedSearch}` : '';
}
// App-internal referrers (the SPA-tracked path) are relative and still carry
// code/state, so they cannot be skipped — split them without URL parsing.
function redactRelativeUrl(url) {
    const hashIndex = url.indexOf('#');
    const hash = hashIndex === -1 ? '' : url.slice(hashIndex);
    const pathAndSearch = hashIndex === -1 ? url : url.slice(0, hashIndex);
    const searchIndex = pathAndSearch.indexOf('?');
    if (searchIndex === -1) {
        return url;
    }
    const pathname = pathAndSearch.slice(0, searchIndex);
    return pathname + redactTelemetrySearchParams(pathname, pathAndSearch.slice(searchIndex)) + hash;
}
export function redactTelemetryUrl(url) {
    if (url.startsWith('/')) {
        return redactRelativeUrl(url);
    }
    try {
        const parsed = new URL(url);
        const redactedSearch = redactTelemetrySearchParams(parsed.pathname, parsed.search);
        if (redactedSearch === parsed.search) {
            return url;
        }
        parsed.search = redactedSearch;
        return parsed.toString();
    }
    catch {
        return url;
    }
}
//# sourceMappingURL=redactAuthParams.js.map